import { styleImageGeneration } from "../../types/imageStudio/images";

export const imageDescriptionPrompt = "Create a vivid, highly detailed visual description of the image that captures the scene, atmosphere, emotional mood, lighting, composition, textures, and dominant color palette. Describe the setting in a cinematic and immersive way so the reader can clearly imagine the image without seeing it. Include nuances such as contrasts, shadows, color temperature, depth, and visual style. Focus purely on the visual impression and aesthetic feeling of the image. Return only the final descriptive text without explanations, formatting, or additional commentary."

const textLayerStyleBasePositive = `
    Apply the style only to the text itself.
    Preserve the exact text content, spelling, letter shapes, layout, size, position, alignment, and readability.
    Do not change the text into another object.
    Do not add extra letters, words, symbols, logos, decorations, or unrelated elements.

    Respect the existing text color as the main color direction.
    If the selected text color is visible, adapt the material, gradient, glow, reflections, or texture to harmonize with that color.

    Keep the background transparent if no background color is selected.
    If a background color already exists, keep it simple and compatible with the text style.
    Do not generate a new scene, environment, object, landscape, room, or decorative background.
`;

const textLayerStyleBaseNegative = `
    changed text,
    misspelled text,
    extra letters,
    extra words,
    unreadable text,
    distorted typography,
    replaced subject,
    new background scene,
    complex environment,
    added objects,
    logos,
    watermark,
    frame,
    border unless requested,
    opaque background when transparent background is expected
`;

const buildTextStyleInstruction = (
    textPositive: string,
    imagePositive: string,
    negative: string,
    isTextLayer: boolean
) => {
    return isTextLayer
        ? {
            positive_prompt: `
                ${textLayerStyleBasePositive}
                ${textPositive}
            `,
            negative_prompt: `
                ${textLayerStyleBaseNegative}
                ${negative}
            `
        }
        : {
            positive_prompt: imagePositive,
            negative_prompt: negative
        };
};

export const styleDescriptionPrompt = (
    positivePrompt: string,
    negativePrompt: string | null,
    style: styleImageGeneration,
    reference: boolean = false,
    isTextLayer: boolean = false
) => {
    const baseInstruction = `
        Use the original prompt as the main source of truth.
        If a reference image is provided, preserve its subject, composition, proportions, environment, perspective, and core visual identity.
        If no reference image is provided, create the image based only on the prompt.
        Apply the selected style as a visual treatment only.
        Do not replace the original concept with unrelated subjects, locations, objects, or scene elements.
    `;

    let styleInstruction = {
        positive_prompt: '',
        negative_prompt: ''
    };

    switch (style) {
        case 'none':
            styleInstruction.positive_prompt = positivePrompt;
            styleInstruction.negative_prompt = negativePrompt ?? '';
            return styleInstruction;

        case 'realistic':
            styleInstruction = {
                positive_prompt: `
                    realistic photographic style,
                    natural proportions,
                    believable lighting,
                    authentic materials,
                    realistic textures,
                    accurate shadows and reflections,
                    natural colors,
                    lifelike details,
                    professional high-resolution photography look
                `,
                negative_prompt: `
                    cartoon,
                    anime,
                    comic style,
                    painterly brush strokes,
                    watercolor,
                    abstract rendering,
                    exaggerated proportions,
                    unrealistic anatomy,
                    artificial plastic textures,
                    oversaturated colors,
                    low detail,
                    unrealistic lighting
                `
            };
            break;

        case 'artistic':
            styleInstruction = {
                positive_prompt: `
                    artistic visual interpretation,
                    expressive composition,
                    creative but coherent lighting,
                    rich atmosphere,
                    harmonious colors,
                    painterly visual quality,
                    emotional mood,
                    visually striking details
                `,
                negative_prompt: `
                    photorealistic rendering,
                    sterile appearance,
                    flat composition,
                    dull colors,
                    low contrast,
                    lifeless atmosphere,
                    unfinished artwork,
                    low detail,
                    visual clutter
                `
            };
            break;

        case 'anime':
            styleInstruction = {
                positive_prompt: `
                    modern anime illustration style,
                    clear anime line art,
                    clean outlines,
                    expressive shapes,
                    stylized but recognizable forms,
                    smooth cel shading,
                    soft gradient shadows,
                    vibrant controlled colors,
                    detailed anime background rendering,
                    polished Japanese animation key visual look,
                    keep the original subject and setting recognizable
                `,
                negative_prompt: `
                    photorealistic rendering,
                    realistic skin textures,
                    photographic lighting,
                    realistic camera artifacts,
                    rough sketch lines,
                    painterly oil brush strokes,
                    blurry line art,
                    low detail,
                    distorted anatomy,
                    messy outlines
                `
            };
            break;

        case 'digital art':
            styleInstruction = {
                positive_prompt: `
                    high-quality digital art style,
                    polished concept art rendering,
                    detailed surfaces,
                    cinematic lighting,
                    immersive depth,
                    sharp visual clarity,
                    rich colors,
                    professional digital painting finish
                `,
                negative_prompt: `
                    low resolution,
                    blurry details,
                    unfinished artwork,
                    sketch quality,
                    noisy textures,
                    muddy colors,
                    flat lighting,
                    low contrast,
                    photorealistic camera artifacts,
                    watercolor effects,
                    charcoal strokes
                `
            };
            break;

        case 'oil-painting':
            styleInstruction = {
                positive_prompt: `
                    traditional oil painting style,
                    visible brush strokes,
                    layered paint texture,
                    rich pigments,
                    canvas-like surface,
                    dramatic fine-art lighting,
                    classical painterly atmosphere,
                    museum-quality artwork look
                `,
                negative_prompt: `
                    photorealistic rendering,
                    digital painting appearance,
                    watercolor effects,
                    cel shading,
                    comic outlines,
                    smooth plastic surfaces,
                    CGI rendering,
                    flat colors,
                    sketch appearance,
                    low detail,
                    blurry brushwork,
                    modern graphic design style
                `
            };
            break;

        case 'water-color':
            styleInstruction = {
                positive_prompt: `
                    watercolor painting style,
                    soft transparent color washes,
                    gentle pigment bleeding,
                    light paper texture,
                    delicate brushwork,
                    soft edges,
                    airy atmosphere,
                    handmade illustration feeling
                `,
                negative_prompt: `
                    photorealistic rendering,
                    hard edges,
                    sharp digital outlines,
                    oil painting texture,
                    heavy brush strokes,
                    comic style,
                    cel shading,
                    CGI rendering,
                    plastic surfaces,
                    harsh contrast,
                    oversaturated colors,
                    low quality watercolor texture
                `
            };
            break;

        case '3d render':
            styleInstruction = {
                positive_prompt: `
                    high-quality 3D render style,
                    physically based materials,
                    realistic CGI lighting,
                    ray-traced reflections,
                    global illumination,
                    clean geometry,
                    detailed surfaces,
                    professional 3D visualization look
                `,
                negative_prompt: `
                    sketch style,
                    watercolor,
                    oil painting,
                    comic rendering,
                    anime line art,
                    visible brush strokes,
                    rough textures,
                    low polygon appearance,
                    poor lighting,
                    low detail,
                    blurry rendering,
                    unrealistic materials
                `
            };
            break;

        case 'cyberpunkt':
            styleInstruction = {
                positive_prompt: `
                    cyberpunk-inspired visual treatment,
                    futuristic color grading,
                    neon accent lighting,
                    subtle glowing details,
                    reflective surfaces where appropriate,
                    high-tech mood,
                    cinematic sci-fi atmosphere,
                    keep the original scene intact,
                    do not turn the image into a cyberpunk city unless the prompt explicitly asks for it
                `,
                negative_prompt: `
                    medieval architecture,
                    fantasy creatures,
                    historical clothing,
                    steampunk machinery,
                    rustic village aesthetic,
                    low-tech environment,
                    cartoon rendering,
                    watercolor painting,
                    oil painting,
                    unrelated futuristic cities,
                    giant neon skylines,
                    excessive sci-fi elements,
                    scene replacement,
                    subject replacement
                `
            };
            break;
        
        case 'comic':
            styleInstruction = {
                positive_prompt: `
                    modern comic book illustration style,
                    bold clean ink outlines,
                    stylized comic shading,
                    dynamic but readable composition,
                    graphic novel aesthetic,
                    expressive contrast,
                    detailed comic-style rendering,
                    vibrant but balanced colors,
                    subtle halftone textures,
                    cinematic comic lighting,
                    illustrated action-oriented visual treatment,
                    handcrafted comic artwork appearance,
                    preserve the original subject, environment, and composition,
                    do not replace the original scene with unrelated comic elements
                `,
                negative_prompt: `
                    photorealistic rendering,
                    realistic skin textures,
                    watercolor painting,
                    oil painting,
                    rough sketch style,
                    blurry outlines,
                    soft undefined edges,
                    CGI rendering,
                    realistic photography,
                    muted colors,
                    low contrast,
                    unfinished comic artwork,
                    low detail
                `
            };
            break;

        case 'cinematic':
            styleInstruction = {
                positive_prompt: `
                    cinematic movie-like visual style,
                    dramatic but realistic lighting,
                    cinematic color grading,
                    immersive atmosphere,
                    film still composition,
                    realistic depth of field,
                    emotionally powerful framing,
                    subtle film grain,
                    professional movie production aesthetic,
                    preserve the original scene and subject composition
                `,
                negative_prompt: `
                    flat lighting,
                    snapshot photography,
                    amateur composition,
                    poor framing,
                    harsh digital appearance,
                    cartoon rendering,
                    comic style,
                    anime style,
                    low detail,
                    unrealistic perspective,
                    washed out colors,
                    cluttered composition,
                    weak atmosphere
                `
            };
            break;

        case 'minimalistic':
            styleInstruction = {
                positive_prompt: `
                    clean minimalistic design style,
                    simplified composition,
                    elegant visual balance,
                    subtle color palette,
                    refined shapes,
                    uncluttered environment,
                    modern aesthetic,
                    negative space usage,
                    soft lighting,
                    preserve the original subject while simplifying visual complexity
                `,
                negative_prompt: `
                    visual clutter,
                    excessive detail,
                    overcrowded composition,
                    noisy background,
                    chaotic scene elements,
                    unnecessary objects,
                    heavy textures,
                    excessive ornamentation,
                    complex patterns,
                    busy color palette,
                    dramatic visual overload
                `
            };
            break;

        case 'vintage':
            styleInstruction = {
                positive_prompt: `
                    vintage visual style,
                    retro photographic atmosphere,
                    aged film tones,
                    soft faded colors,
                    nostalgic mood,
                    analog photography aesthetic,
                    subtle grain and imperfections,
                    timeless composition,
                    classic visual treatment,
                    preserve the original scene and composition
                `,
                negative_prompt: `
                    modern digital appearance,
                    neon colors,
                    futuristic elements,
                    ultra-clean rendering,
                    excessive sharpness,
                    modern camera processing,
                    HDR overprocessing,
                    glossy CGI materials,
                    cyberpunk aesthetics,
                    sterile digital look,
                    oversaturated colors
                `
            };
            break;

        case 'noir':
            styleInstruction = {
                positive_prompt: `
                    film noir inspired visual style,
                    dramatic shadows,
                    moody black and white contrast,
                    low-key lighting,
                    atmospheric darkness,
                    cinematic mystery mood,
                    strong silhouettes,
                    realistic noir photography aesthetic,
                    preserve the original scene structure and subject
                `,
                negative_prompt: `
                    colorful vibrant palette,
                    cheerful atmosphere,
                    bright daylight look,
                    fantasy elements,
                    cartoon rendering,
                    anime style,
                    soft contrast,
                    flat lighting,
                    oversaturated colors,
                    playful mood,
                    modern social media aesthetic,
                    low drama
                `
            };
            break;

        case 'fantasy':
            styleInstruction = {
                positive_prompt: `
                    transform the subject itself into a fantasy-inspired version,
                    make the subject look magical, exotic, enchanting, and otherworldly,
                    redesign materials, shapes, surfaces, ornaments, and details to naturally belong in a fantasy world,
                    incorporate mystical and arcane design elements appropriate to the subject,
                    enchanted materials such as glowing crystals, magical metals, ancient wood, ethereal fabrics, runes, or organic fantasy details where appropriate,
                    imaginative handcrafted fantasy design,
                    elegant supernatural details,
                    magical atmosphere,
                    cinematic fantasy lighting,
                    mystical environmental details,
                    epic and enchanting mood,
                    immersive fantasy world-building aesthetic,
                    rich detailed fantasy textures,
                    cohesive fantasy color palette,
                    make the subject and environment feel like they belong to the same fantasy world,
                    preserve the core identity, purpose, and recognizable form of the original subject while creatively adapting its appearance to the fantasy setting
                `,
                negative_prompt: `
                    mundane everyday appearance,
                    ordinary unmodified subject,
                    plain realistic materials,
                    generic contemporary design,
                    modern urban environment,
                    realistic contemporary technology,
                    office setting,
                    sterile realism,
                    documentary style,
                    industrial realism,
                    minimalist aesthetic,
                    flat visual storytelling,
                    low imagination,
                    disconnected fantasy background with an ordinary subject,
                    fantasy environment without fantasy adaptation of the subject,
                    cyberpunk,
                    sci-fi technology
                `
            };
            break;

        case 'gothic':
            styleInstruction = {
                positive_prompt: `
                    transform the requested subject itself into a gothic-inspired version,
                    make the subject look dark, mysterious, elegant, dramatic, and atmospheric,
                    preserve the original subject category, recognizable form, purpose, composition, pose, and subject count,
                    apply the gothic aesthetic directly to the requested subject rather than only changing the background, lighting, or atmosphere,
                    redesign the subject's materials, surfaces, shapes, ornaments, textures, and details so it naturally belongs to a gothic world,
                    use dark gothic design language and ornate historical-inspired details appropriate to the requested subject,
                    incorporate refined elements such as aged dark metal, blackened wood, weathered stone, dark leather, subtle filigree, intricate engravings, antique details, elegant ornamental patterns, and restrained decorative motifs where appropriate,
                    use sophisticated dark craftsmanship and detailed gothic textures,
                    create a dark elegant mood,
                    use moody cinematic lighting,
                    dramatic shadows,
                    deep muted colors,
                    cold desaturated tones,
                    rich blacks,
                    restrained purple, burgundy, silver, or dark red accents where appropriate,
                    refined contrast,
                    subtle atmospheric fog and depth where appropriate,
                    mysterious and melancholic atmosphere,
                    dark romantic visual character without turning the scene into a portrait or fashion image,
                    make the subject and environment feel like they naturally belong to the same gothic world,
                    keep the original scene recognizable while creatively adapting its visual appearance to the gothic aesthetic,
                    if the prompt is minimal, keep the requested subject simple, central, and clearly recognizable while applying the gothic styling directly to it,

                    do not introduce people, human figures, faces, portraits, characters, or additional subjects unless they are explicitly requested in the prompt or already present in the source image,
                    if a person is explicitly requested or already present in the source image, preserve that person's identity, pose, and role while applying the gothic styling to them,
                    if no person is requested and no person is present in the source image, keep the generated result completely free of people, faces, portraits, and human figures,
                    do not change the requested subject into a different subject category,
                    do not replace the requested object or scene with unrelated gothic scenery
                `,
                negative_prompt: `
                    ordinary unmodified subject,
                    mundane everyday appearance,
                    plain contemporary materials,
                    generic modern design,
                    bright cheerful aesthetic,
                    vibrant playful colors,
                    warm happy atmosphere,
                    sterile realism,
                    documentary style,
                    minimalist aesthetic,
                    flat lighting,
                    flat visual storytelling,

                    unrequested people,
                    unrequested women,
                    unrequested men,
                    unrequested human figures,
                    unrequested portraits,
                    unrequested faces,
                    unrequested characters,
                    gothic woman,
                    gothic man,
                    gothic fashion model,
                    portrait photography,
                    fashion portrait,
                    adding a person when none is requested,
                    adding human subjects not present in the source image,
                    replacing the requested subject with a person,

                    gothic background with an ordinary unchanged subject,
                    dark lighting applied to an otherwise unchanged subject,
                    unrelated gothic scenery,
                    replacing the original subject,
                    replacing the original scene,
                    changing the subject category,
                    additional unrelated main subjects,

                    churches,
                    cathedrals,
                    castles,
                    cemeteries,
                    gravestones,
                    gargoyles,
                    stained glass windows,
                    religious architecture,
                    medieval architecture,
                    gothic buildings,
                    gothic cathedrals,
                    fantasy castles,
                    dark temples,
                    excessive architectural elements,

                    horror monsters,
                    zombies,
                    demons,
                    fantasy creatures,
                    gore,
                    grotesque horror,
                    body horror,
                    excessive horror imagery
                `
            };
            break;

        case 'charcoal':
            styleInstruction = {
                positive_prompt: `
                    charcoal drawing style,
                    expressive charcoal strokes,
                    textured shading,
                    rough sketch aesthetics,
                    monochromatic artistic rendering,
                    handcrafted illustration appearance,
                    dramatic contrast,
                    paper texture details,
                    preserve the original composition and subject shapes
                `,
                negative_prompt: `
                    photorealistic rendering,
                    colorful illustration,
                    watercolor painting,
                    oil painting,
                    glossy surfaces,
                    CGI rendering,
                    smooth gradients,
                    digital painting appearance,
                    clean vector graphics,
                    vibrant colors,
                    plastic textures,
                    realistic photography
                `
            };
            break;

        case 'line-art':
            styleInstruction = {
                positive_prompt: `
                    clean line art illustration style,
                    precise outlines,
                    elegant contour work,
                    highly readable shapes,
                    refined ink drawing appearance,
                    minimal shading,
                    simplified visual design,
                    preserve the original composition and subject proportions
                `,
                negative_prompt: `
                    photorealistic rendering,
                    heavy shading,
                    painterly brush strokes,
                    watercolor textures,
                    oil painting textures,
                    realistic skin textures,
                    excessive colors,
                    cluttered composition,
                    CGI rendering,
                    blurry outlines,
                    sketch noise,
                    low readability
                `
            };
            break;

        case 'brick-toy':
            styleInstruction = {
                positive_prompt: `
                    stylized brick toy construction style,
                    block-built appearance,
                    recognizable toy bricks,
                    modular geometric design,
                    colorful plastic toy materials,
                    playful miniature world aesthetic,
                    detailed brick-built structures,
                    clean toy-like rendering,
                    preserve the original scene composition and subject identity,
                    transform objects into constructions made from interlocking toy bricks
                `,
                negative_prompt: `
                    photorealistic rendering,
                    realistic materials,
                    organic surfaces,
                    soft fabric textures,
                    smooth sculpted geometry,
                    realistic skin,
                    realistic vegetation,
                    oil painting,
                    watercolor,
                    sketch style,
                    abstract art,
                    unrelated toy characters,
                    random toy scenery
                `
            };
            break;

        case 'papercraft':
            styleInstruction = {
                positive_prompt: `
                    three-dimensional papercraft model style,
                    handcrafted paper sculpture appearance,
                    layered paper construction,
                    folded paper geometry,
                    visible paper edges and seams,
                    assembled paper model aesthetic,
                    realistic paper materials,
                    soft paper shadows,
                    dimensional cut-paper depth,
                    detailed handcrafted paper artwork,
                    transform subjects into believable 3D paper creations,
                    preserve the original subject, composition and scene layout
                `,
                negative_prompt: `
                    flat collage appearance,
                    flat paper textures,
                    photorealistic rendering,
                    plastic materials,
                    metallic materials,
                    realistic skin textures,
                    CGI rendering,
                    watercolor painting,
                    oil painting,
                    clay sculpture,
                    wooden models,
                    fabric materials,
                    low-detail paper cutouts
                `
            };
            break;

        case 'surreal':
            styleInstruction = {
                positive_prompt: `
                    transform the requested subject and scene itself into a surreal artistic interpretation,
                    make the subject look dreamlike, imaginative, uncanny, unexpected, and visually impossible while remaining recognizable,
                    preserve the original subject category, core identity, recognizable form, composition, pose, scene structure, and subject count,
                    apply the surreal aesthetic directly to the requested subject rather than only changing the background, lighting, or atmosphere,
                    creatively distort, transform, exaggerate, combine, fragment, stretch, bend, float, melt, repeat, or rearrange elements of the subject where appropriate,
                    introduce impossible but visually coherent forms, proportions, materials, spatial relationships, perspectives, and physical behavior,
                    reinterpret familiar objects and environments through dream logic and imaginative visual transformations,
                    use unexpected combinations of materials, textures, scale, gravity, reflections, geometry, and spatial relationships where appropriate,
                    create unusual but intentional visual juxtapositions,
                    incorporate symbolic and metaphorical visual details appropriate to the requested subject,
                    use dreamlike transitions between objects, surfaces, and environments,
                    create impossible architecture, geometry, perspective, gravity, scale, or spatial relationships where appropriate to the scene,
                    cinematic surrealism,
                    dreamlike atmosphere,
                    mysterious and emotionally abstract mood,
                    imaginative environmental distortions,
                    symbolic visual storytelling,
                    unusual but coherent composition,
                    sophisticated surreal visual language,
                    detailed artistic textures,
                    visually striking transformations,
                    make the subject and environment feel like parts of the same surreal dream world,
                    maintain visual coherence despite impossible or dreamlike elements,
                    keep the requested subject clearly identifiable while creatively transforming its appearance and physical logic,
                    if the prompt is minimal, keep the requested subject simple, central, and recognizable while applying clearly visible surreal transformations directly to it,

                    do not introduce people, human figures, faces, portraits, characters, or additional main subjects unless they are explicitly requested in the prompt or already present in the source image,
                    if a person is explicitly requested or already present in the source image, preserve that person's identity, pose, and role while applying surreal transformations appropriately,
                    if no person is requested and no person is present in the source image, keep the generated result free of people, faces, portraits, and human figures,
                    do not replace the requested subject with an unrelated surreal object or scene,
                    do not change the fundamental subject category,
                    surrealism should transform the requested content rather than replace it
                `,
                negative_prompt: `
                    strict realism,
                    documentary photography,
                    ordinary everyday appearance,
                    completely realistic visual logic,
                    mundane environments,
                    conventional physical behavior,
                    completely normal proportions,
                    completely ordinary materials,
                    technical illustrations,
                    sterile compositions,
                    flat storytelling,
                    low imagination,
                    generic visual treatment,

                    unchanged subject placed in a surreal background,
                    surreal background with an ordinary unmodified subject,
                    surreal atmosphere applied without transforming the requested subject,
                    replacing the requested subject,
                    replacing the original scene,
                    changing the subject category,
                    unrelated surreal scenery,
                    unrelated additional main subjects,

                    random visual chaos,
                    meaningless distortion,
                    incoherent composition,
                    arbitrary object placement,
                    visual noise,
                    excessive fragmentation,
                    completely unrecognizable subjects,
                    destroyed subject identity,
                    accidental-looking deformation,

                    unrequested people,
                    unrequested women,
                    unrequested men,
                    unrequested human figures,
                    unrequested portraits,
                    unrequested faces,
                    unrequested characters,
                    adding a person when none is requested,
                    adding human subjects not present in the source image,
                    replacing the requested subject with a person,

                    generic fantasy aesthetic,
                    generic sci-fi aesthetic,
                    generic horror aesthetic,
                    gore,
                    grotesque horror
                `
            };
            break;

        case 'photographic':
            styleInstruction = {
                positive_prompt: `
                    professional photographic style,
                    highly realistic camera rendering,
                    natural lens behavior,
                    realistic lighting and exposure,
                    authentic materials and textures,
                    ultra high-resolution photography look,
                    documentary-style realism,
                    preserve the original scene and composition
                `,
                negative_prompt: `
                    painting,
                    watercolor,
                    oil painting,
                    sketch style,
                    comic rendering,
                    anime style,
                    cartoon appearance,
                    CGI rendering,
                    unrealistic proportions,
                    artificial textures,
                    excessive stylization,
                    fantasy effects,
                    surreal distortions
                `
            };
            break;

        case 'storybook':
            styleInstruction = {
                positive_prompt: `
                    illustrated storybook visual style,
                    warm narrative atmosphere,
                    soft painterly rendering,
                    whimsical details,
                    emotionally inviting composition,
                    charming illustrated textures,
                    cinematic children's book aesthetic,
                    preserve the original subject and environment while adding storybook styling
                `,
                negative_prompt: `
                    photorealistic rendering,
                    harsh realism,
                    dark horror atmosphere,
                    gritty documentary style,
                    realistic photography,
                    overly detailed skin textures,
                    industrial appearance,
                    sterile compositions,
                    cold technical rendering,
                    excessive contrast,
                    violent visual themes
                `
            };
            break;
        
        case 'glass-art':
            styleInstruction = {
                positive_prompt: `
                    scene-wide glass artwork transformation,
                    transform the entire image into a sophisticated glass art creation,
                    convert subjects, environment, background, objects and surfaces into elegant glass materials,
                    translucent glass structures,
                    crystal-inspired artistic rendering,
                    refractive transparent materials throughout the scene,
                    realistic reflections and refractions,
                    colorful light dispersion,
                    luminous highlights,
                    layered transparent depth,
                    premium handcrafted glass sculpture aesthetic,
                    cinematic lighting interacting with glass surfaces,
                    preserve the original composition, scene layout and subject identity,
                    make the entire artwork appear constructed from artistic glass rather than only a single object
                `,
                negative_prompt: `
                    photorealistic materials,
                    opaque surfaces,
                    matte materials,
                    fabric textures,
                    wood textures,
                    stone textures,
                    metallic surfaces,
                    plastic materials,
                    realistic skin textures,
                    watercolor painting,
                    oil painting,
                    sketch style,
                    transforming only the main subject into glass,
                    isolated glass object on a normal background,
                    partially transformed scene
                `
            };
            break;

        case 'golden-art':
            styleInstruction = reference
                ? {
                    positive_prompt: `
                        transform the exact subject from the reference image into flowing liquid gold,
                        preserve the original subject identity,
                        preserve silhouette,
                        preserve pose,
                        preserve composition,
                        preserve background structure,
                        molten gold surfaces,
                        liquid metal appearance,
                        flowing golden material,
                        champagne-gold highlights,
                        reflective metallic surfaces,
                        elegant fluid transitions,
                        partially melting golden forms,
                        cinematic lighting,
                        premium luxury artwork aesthetic,
                        the subject must remain clearly recognizable
                    `,
                    negative_prompt: `
                        castles,
                        palaces,
                        crowns,
                        jewelry,
                        treasure,
                        gold coins,
                        kings,
                        queens,
                        royal clothing,
                        luxury interiors,
                        fantasy scenery,
                        replacing the subject,
                        creating new characters,
                        changing the composition
                    `
                }
                : {
                    positive_prompt: `
                        create the exact subject described in the prompt,
                        transform the subject into flowing liquid gold,
                        molten metallic appearance,
                        liquid gold material,
                        reflective champagne-gold highlights,
                        elegant golden fluidity,
                        partially melting golden surfaces,
                        cinematic reflections,
                        preserve the requested subject as the main focus,
                        simple supporting environment,
                        premium artistic gold sculpture aesthetic
                    `,
                    negative_prompt: `
                        castles,
                        temples,
                        palaces,
                        treasure rooms,
                        gold coins,
                        crowns,
                        jewelry,
                        royal clothing,
                        kings,
                        queens,
                        fantasy kingdoms,
                        luxury interiors,
                        additional characters,
                        unrelated golden objects
                    `
                };
            break;
        
        case 'block-world':
            styleInstruction = reference
                ? {
                    positive_prompt: `
                        reinterpret the reference image as Minecraft-inspired block-world artwork,
                        preserve the overall composition,
                        preserve the subject identity,
                        preserve the pose,
                        preserve the scene layout,
                        use large recognizable cubic shapes,
                        simplified Minecraft-like geometry,
                        chunky blocks,
                        low-detail voxel construction,
                        bright game-art colors,
                        clean block structures,
                        recognizable Minecraft-inspired visual language,
                        avoid excessive voxel detail,
                        focus on readability rather than accuracy
                    `,
                    negative_prompt: `
                        photorealism,
                        realistic materials,
                        tiny voxels,
                        highly detailed voxel sculptures,
                        pixel-perfect reconstruction,
                        smooth geometry,
                        realistic textures,
                        cinematic rendering,
                        complex voxel art,
                        realistic humans,
                        realistic faces
                    `
                }
                : {
                    positive_prompt: `
                        Minecraft-inspired block-world style,
                        chunky cubic construction,
                        large simple blocks,
                        colorful voxel world,
                        game-art appearance,
                        bright flat colors,
                        clean silhouettes,
                        recognizable subject,
                        stylized Minecraft aesthetic,
                        simple block-built environment,
                        preserve subject readability
                    `,
                    negative_prompt: `
                        photorealism,
                        realistic materials,
                        tiny voxels,
                        complex voxel sculptures,
                        cinematic realism,
                        smooth geometry,
                        realistic textures,
                        game HUD,
                        UI elements,
                        menus,
                        weapons,
                        unrelated sandbox objects
                    `
                };
            break;
        
        case 'wooden-blocks':
            styleInstruction = reference
                ? {
                    positive_prompt: `
                        rebuild the reference subject from handcrafted wooden blocks,
                        preserve the original silhouette,
                        preserve composition,
                        preserve pose,
                        preserve scene structure,
                        stacked wooden block construction,
                        visible seams between blocks,
                        warm natural wood grain,
                        toy-like craftsmanship,
                        simplified wooden geometry,
                        handcrafted wooden model appearance,
                        recognizable wooden block sculpture
                    `,
                    negative_prompt: `
                        realistic skin,
                        fabric textures,
                        metallic surfaces,
                        plastic materials,
                        realistic photography,
                        smooth geometry,
                        carved wooden statues,
                        wooden furniture,
                        shelves,
                        tools,
                        toy packaging,
                        unrelated wooden objects
                    `
                }
                : {
                    positive_prompt: `
                        create the requested subject from stacked wooden blocks,
                        handcrafted wooden construction,
                        visible wood grain,
                        warm wooden materials,
                        simple geometric block structure,
                        toy-like appearance,
                        clean wooden craftsmanship,
                        recognizable subject made from wooden blocks,
                        simple supporting environment
                    `,
                    negative_prompt: `
                        realistic materials,
                        fabric textures,
                        metallic surfaces,
                        plastic toys,
                        carved sculptures,
                        furniture,
                        shelves,
                        workshops,
                        tools,
                        packaging,
                        unrelated wooden scenery,
                        realistic photography
                    `
                };
            break;

        // text styles
        case 'textMetallic':
            styleInstruction = buildTextStyleInstruction(
                `
                    Transform the text into a premium metallic text effect.
                    Use brushed metal, chrome-like gradients, polished steel reflections, and smooth metallic highlights.
                    Let the metallic reflections harmonize with the selected text color.
                `,
                `
                    premium metallic material style,
                    brushed metal surfaces,
                    reflective chrome gradients,
                    polished steel appearance,
                    smooth metallic transitions,
                    realistic metallic reflections,
                    elegant silver and titanium tones,
                    premium industrial design aesthetic,
                    preserve the original subject and composition
                `,
                `
                    rust,
                    corrosion,
                    dirt,
                    matte materials,
                    plastic appearance,
                    fabric textures,
                    wood textures,
                    stone materials,
                    low-quality metallic surfaces,
                    rough unfinished metal
                `,
                isTextLayer
            );
            break;

        case 'textFlower':
            styleInstruction = buildTextStyleInstruction(
                `
                    Transform the text into floral typography.
                    The letters should be formed from flowers, petals, leaves, vines, and botanical details.
                    Keep the text readable and preserve the letter silhouettes.
                    Use the selected text color as the main floral color direction where possible.
                `,
                `
                    floral artwork style,
                    composed of flowers and petals,
                    blooming botanical details,
                    colorful flower arrangements,
                    elegant organic structures,
                    lush natural composition,
                    detailed blossoms,
                    decorative floral design,
                    preserve the original subject shape and readability
                `,
                `
                    dead flowers,
                    dry plants,
                    metallic materials,
                    plastic flowers,
                    artificial decoration,
                    stone textures,
                    industrial elements,
                    dark horror flowers,
                    cluttered vegetation
                `,
                isTextLayer
            );
            break;

        case 'textGolden':
            styleInstruction = buildTextStyleInstruction(
                `
                    Transform the text into flowing liquid-gold typography.
                    The letters should look molten, reflective, elegant, and slightly melting.
                    Use the selected text color as the base tone for the golden tint where possible.
                    Keep the text readable and do not add royal scenery or treasure elements.
                `,
                `
                    flowing liquid gold style,
                    molten golden material,
                    reflective champagne-gold highlights,
                    elegant metallic fluidity,
                    partially melting gold surfaces,
                    luxurious golden appearance,
                    premium artistic gold treatment,
                    preserve the original subject shape and readability
                `,
                `
                    castles,
                    crowns,
                    treasure,
                    jewelry,
                    gold coins,
                    luxury interiors,
                    royal scenery,
                    unrelated golden objects,
                    replacing the original subject
                `,
                isTextLayer
            );
            break;

        case 'textDark':
            styleInstruction = buildTextStyleInstruction(
                `
                    Transform the text into a dark atmospheric typography effect.
                    Use deep shadows, dramatic contrast, subtle shadow depth, and a mysterious elegant mood.
                    Keep the letters readable and preserve the existing text color direction.
                `,
                `
                    dark atmospheric styling,
                    deep shadows,
                    dramatic contrast,
                    dark elegant materials,
                    mysterious mood,
                    subtle fog effects,
                    cinematic darkness,
                    gothic-inspired atmosphere,
                    preserve the original subject shape and readability
                `,
                `
                    bright cheerful colors,
                    fantasy castles,
                    horror monsters,
                    zombies,
                    excessive blood,
                    bright daylight,
                    playful cartoon appearance,
                    colorful decorations
                `,
                isTextLayer
            );
            break;

        case 'textTransition':
            styleInstruction = buildTextStyleInstruction(
                `
                    Transform the text into a smooth gradient typography effect.
                    Apply elegant color transitions inside the letters only.
                    Use the selected text color as one of the main gradient colors where possible.
                    Keep the text readable and preserve the exact letter shapes.
                `,
                `
                    smooth gradient transition style,
                    flowing color blends,
                    elegant multi-color gradients,
                    seamless color transitions,
                    vibrant but harmonious palette,
                    modern digital artwork appearance,
                    premium gradient design,
                    preserve the original subject shape and readability
                `,
                `
                    hard color boundaries,
                    random color noise,
                    muddy colors,
                    low contrast,
                    washed-out gradients,
                    flat monochrome appearance,
                    inconsistent color transitions
                `,
                isTextLayer
            );
            break;

        case 'textNeon':
            styleInstruction = buildTextStyleInstruction(
                `
                    Transform the text into glowing neon typography.
                    Apply neon tube lighting, luminous edges, soft bloom, and electric glow only to the letters.
                    Let the neon color harmonize with the selected text color.
                    Keep the background transparent unless a background color already exists.
                `,
                `
                    vibrant neon light style,
                    glowing illuminated surfaces,
                    colorful neon tubes,
                    bright emissive lighting,
                    electric glow effects,
                    futuristic nightlife aesthetic,
                    vivid color gradients,
                    soft bloom lighting,
                    luminous edges,
                    preserve the original subject shape and readability
                `,
                `
                    dark unlit surfaces,
                    matte materials,
                    realistic photography,
                    stone textures,
                    wood textures,
                    low contrast,
                    dull colors,
                    broken neon signs,
                    rust,
                    dirt,
                    weak lighting
                `,
                isTextLayer
            );
            break;

        case 'textCrystal':
            styleInstruction = buildTextStyleInstruction(
                `
                    Transform the text into crystal gemstone typography.
                    Use transparent crystalline facets, sparkling highlights, refractions, and gemstone-like depth.
                    Adapt the crystal color to the selected text color where possible.
                    Keep the text readable and apply the crystal effect only to the letters.
                `,
                `
                    crystal gemstone style,
                    transparent crystalline materials,
                    diamond-like reflections,
                    refractive surfaces,
                    elegant gemstone appearance,
                    sparkling highlights,
                    luxurious crystal structure,
                    faceted glass-like geometry,
                    colorful light refractions,
                    preserve the original subject shape and readability
                `,
                `
                    opaque materials,
                    plastic surfaces,
                    wood textures,
                    fabric textures,
                    rough stone,
                    dirt,
                    scratches,
                    cloudy surfaces,
                    low transparency,
                    matte appearance
                `,
                isTextLayer
            );
            break;

        case 'textFire':
            styleInstruction = buildTextStyleInstruction(
                `
                    Transform the text into fiery typography.
                    Apply flames, glowing embers, sparks, molten heat effects, and warm fire colors only to the letters.
                    Keep the text readable and preserve the letter silhouettes.
                    Do not fill the background with fire unless a background color already exists.
                `,
                `
                    blazing fire style,
                    burning flames,
                    glowing embers,
                    molten heat effects,
                    fiery energy,
                    orange and red flame colors,
                    dynamic fire motion,
                    sparks and glowing particles,
                    intense heat atmosphere,
                    preserve the original subject shape and readability
                `,
                `
                    water effects,
                    ice,
                    snow,
                    smoke only,
                    cold atmosphere,
                    stone materials,
                    metallic surfaces,
                    extinguished fire,
                    dark unlit appearance,
                    weak glow
                `,
                isTextLayer
            );
            break;

        case 'textIce':
            styleInstruction = buildTextStyleInstruction(
                `
                    Transform the text into frozen ice typography.
                    Use crystalline frost, transparent ice surfaces, cold highlights, frosted edges, and snow crystal details.
                    Adapt the icy color tone to the selected text color where possible.
                    Keep the effect only on the letters and preserve readability.
                `,
                `
                    frozen ice style,
                    crystalline frost structures,
                    transparent ice surfaces,
                    icy reflections,
                    frozen textures,
                    snow crystal details,
                    cold blue highlights,
                    winter atmosphere,
                    frosted edges,
                    preserve the original subject shape and readability
                `,
                `
                    fire,
                    lava,
                    molten materials,
                    warm colors,
                    tropical atmosphere,
                    wood textures,
                    rust,
                    dirt,
                    smoke,
                    burning effects
                `,
                isTextLayer
            );
            break;

        case 'textMushroom':
            styleInstruction = buildTextStyleInstruction(
                `
                    Transform ONLY the existing text layer into mushroom-themed typography.

                    The output must contain exactly the same text as the input text layer.
                    Keep the exact same letters, spelling, punctuation, letter order, line breaks, layout, alignment, size, and position.

                    Do not create any additional text.
                    Do not create decorative words, captions, labels, signs, symbols, numbers, handwriting, background text, or pseudo-letters.

                    Apply the mushroom style only inside the existing letter shapes and directly on their edges.
                    Build the existing letters from mushroom caps, mushroom stems, gills, spores, small fungi, and organic fungal textures.

                    Mushroom details must support the existing letter shapes.
                    They must not form new letters, new words, symbols, or readable marks.

                    Keep all non-text areas transparent unless the original text layer already has a background color.
                    Do not generate a forest scene, landscape, environment, signboard, label, poster, or decorative background.

                    The final result must be a clean transparent text layer with only the original text transformed into mushroom typography.
                `,
                `
                    mushroom typography applied only to the existing subject,
                    mushroom caps inside the original shapes,
                    mushroom stems following the original silhouette,
                    fungal gills and spores as texture only,
                    small fungi integrated into the existing form,
                    mushroom-focused organic surface treatment,
                    preserve the original shape and readability
                `,
                `
                    extra text,
                    additional text,
                    new letters,
                    extra letters,
                    fake letters,
                    pseudo letters,
                    random typography,
                    handwriting,
                    captions,
                    labels,
                    signs,
                    logos,
                    symbols,
                    numbers,
                    watermark,
                    background text,
                    decorative words,
                    unreadable typography,
                    changed spelling,
                    changed words,
                    changed letter order,
                    forest background,
                    poster design,
                    signboard,
                    text outside the original layer area
                `,
                isTextLayer
            );
            break;
        
        case 'textNature':
            styleInstruction = buildTextStyleInstruction(
                `
                    Transform the text into organic nature typography.
                    Build the letters from moss, leaves, vines, bark textures, and living plant details.
                    Use the selected text color as the main botanical color direction where possible.
                    Keep the text readable and do not generate a forest background.
                `,
                `
                    natural organic style,
                    moss covered surfaces,
                    growing plants,
                    leaves and vines,
                    tree bark textures,
                    lush vegetation,
                    natural materials,
                    forest-inspired atmosphere,
                    living organic structures,
                    preserve the original subject shape and readability
                `,
                `
                    plastic materials,
                    metallic surfaces,
                    futuristic technology,
                    neon lighting,
                    industrial elements,
                    concrete textures,
                    mechanical parts,
                    artificial decorations,
                    dead vegetation
                `,
                isTextLayer
            );
            break;

        case 'textCandy':
            styleInstruction = buildTextStyleInstruction(
                `
                    Transform the text into candy typography.
                    Use glossy candy coating, sugar glaze, sweet decorative details, and playful confectionery surfaces.
                    Adapt the candy colors to the selected text color where possible.
                    Keep the text readable and apply the style only to the letters.
                `,
                `
                    candy and confectionery style,
                    glossy sweet surfaces,
                    colorful candy coating,
                    sugar glaze textures,
                    playful dessert aesthetic,
                    vibrant candy colors,
                    sweet decorative details,
                    delicious confection appearance,
                    preserve the original subject shape and readability
                `,
                `
                    rust,
                    dirt,
                    stone materials,
                    metallic surfaces,
                    horror elements,
                    dark atmosphere,
                    industrial textures,
                    realistic food packaging,
                    damaged candy
                `,
                isTextLayer
            );
            break;

        case 'textGalaxy':
            styleInstruction = buildTextStyleInstruction(
                `
                    Transform the text into galaxy typography.
                    Fill the letters with nebula colors, star-like highlights, cosmic dust, and deep space gradients.
                    Let the galaxy palette harmonize with the selected text color where possible.
                    Keep the text readable and do not create a full space background unless one already exists.
                `,
                `
                    cosmic galaxy style,
                    colorful nebula clouds,
                    star-filled surfaces,
                    deep space atmosphere,
                    glowing cosmic dust,
                    celestial colors,
                    interstellar light effects,
                    astronomical beauty,
                    universe-inspired artwork,
                    preserve the original subject shape and readability
                `,
                `
                    earthly landscapes,
                    realistic materials,
                    industrial textures,
                    plain colors,
                    empty black surfaces,
                    city environments,
                    mundane objects,
                    low contrast,
                    visual clutter
                `,
                isTextLayer
            );
            break;

        case 'textSmoke':
            styleInstruction = buildTextStyleInstruction(
                `
                    Transform the text into smoke and mist typography.
                    Use swirling vapor, semi-transparent smoky forms, soft fog edges, and drifting particles only inside or around the letters.
                    Keep the text readable and preserve the original layout.
                    Keep the background transparent unless a background color already exists.
                `,
                `
                    smoke and mist style,
                    swirling smoke structures,
                    soft vapor effects,
                    atmospheric fog,
                    drifting particles,
                    elegant smoky forms,
                    semi-transparent materials,
                    mysterious atmosphere,
                    preserve the original subject shape and readability
                `,
                `
                    solid stone materials,
                    metallic surfaces,
                    hard edges,
                    rigid geometry,
                    bright neon colors,
                    fire,
                    ice,
                    sharp mechanical details,
                    dense opaque objects
                `,
                isTextLayer
            );
            break;

        case 'textLiquid':
            styleInstruction = buildTextStyleInstruction(
                `
                    Transform the text into liquid typography.
                    Use flowing fluid surfaces, glossy reflections, droplets, transparent material, and elegant splash-like motion only on the letters.
                    Adapt the liquid color to the selected text color where possible.
                    Keep the text readable and avoid generating a new background.
                `,
                `
                    liquid material style,
                    flowing fluid surfaces,
                    dynamic liquid motion,
                    glossy reflections,
                    water-like appearance,
                    smooth fluid transitions,
                    realistic droplets,
                    elegant splash effects,
                    transparent and reflective materials,
                    preserve the original subject shape and readability
                `,
                `
                    rigid solid materials,
                    stone textures,
                    wood textures,
                    dry surfaces,
                    cracked materials,
                    rough geometry,
                    matte appearance,
                    static objects,
                    low reflections
                `,
                isTextLayer
            );
            break;
    }

    return {
        positive_prompt: `
            ${positivePrompt}

            ${baseInstruction}

            Apply this style:
            ${styleInstruction.positive_prompt}
        `.replace(/\s+/g, " ").trim(),

        negative_prompt: `
            ${negativePrompt ?? ""}

            ${styleInstruction.negative_prompt}
        `.replace(/\s+/g, " ").trim()
    };
};

export const userPromptWithReferenceImageDescription = (
    prompt: string,
    referenceDescription: string
) => {
    return `
        Create a new image generation prompt based primarily on the user's request while using the reference image description only as contextual inspiration for atmosphere, environment, visual mood, lighting style, color harmony, and overall artistic direction.

        User request:
        "${prompt}"

        Reference image context:
        "${referenceDescription}"

        Important:
        The reference description should NOT be interpreted as the actual scene, subject, or composition to recreate. Instead, use it only as a stylistic and atmospheric reference point to understand the visual feeling, environment type, cinematic mood, lighting behavior, realism level, and aesthetic tone.

        The final generated image should be driven by the user's request, not by the literal content of the reference description. Avoid copying objects, characters, compositions, or scene structures from the reference image.

        Focus on:
        - capturing similar atmosphere and emotional tone
        - understanding the environmental feeling and visual context
        - matching cinematic lighting and realistic depth
        - preserving aesthetic coherence and visual quality
        - adapting color grading and mood naturally
        - maintaining high-quality realistic rendering

        Generate a completely new and original image concept that fits the user's request while subtly reflecting the stylistic mood and environmental feeling of the reference image.

        Return only the final optimized image generation prompt.
    `;
};

export const textLayerPrompt = (prompt: string, text: string) => {
    return `
        Convert the provided text layer into a finished image layer.

        User request:
        "${prompt || "No additional user request was provided."}"

        Exact source text:
        "${text}"

        Preserve the original text layer's position, size, proportions, shape, layout, color palette, opacity impression, and overall visual footprint.
        Keep the text content readable and spelled exactly as provided.
        Transform the flat text into a suitable image object or designed graphic element that matches the user's request, but do not change its core silhouette, scale, placement, or color identity unless explicitly requested.
        Do not invent a new background, scene, object, character, or environment.
        The result should fit naturally as an image layer in the existing composition.
    `.replace(/\s+/g, " ").trim();
};

export const drawingLayerPrompt = (prompt: string) => {
    return `
        Convert the provided drawing layer into a finished image layer.

        User request:
        "${prompt || "No additional user request was provided."}"

        Preserve the drawing's original silhouette, position, size, proportions, line direction, shape language, color palette, and visual footprint.
        Keep the same object identity and do not replace it with a different object.
        Use the existing drawing as the strict structure guide and only render it into a polished image object with cleaner edges, coherent material, light detail, and suitable surface texture.
        Do not add a new background, scene, environment, character, or unrelated objects unless explicitly requested.
        Do not change the scale, pose, orientation, or main geometry.
        The result should look like the original drawing faithfully converted into a clean image layer.
    `.replace(/\s+/g, " ").trim();
};

export const variationPrompt = (prompt: string) => {
    const hasPrompt = prompt.trim().length > 0;

    if (!hasPrompt) {
        return `
            Create a single high-quality variation of the provided image.
            No specific user instruction was provided, so vary the image in a general creative way while preserving the original subject, scene identity, and overall intent.
            Adjust elements such as color balance, composition, camera perspective, lighting, atmosphere, depth, background details, texture richness, and visual mood to create a fresh but clearly related version of the original image.
            The result should feel like an alternative version of the same image, not a completely different scene. Keep the main subject recognizable and maintain the original visual direction while improving artistic quality, balance, and detail.
            Do not create multiple variations. Generate only one varied image.
        `;
    }

    return `
        Create a single high-quality variation of the provided image based on this user request:
        "${prompt}"
        Only vary the specific object, subject, area, style detail, or visual element described in the user request. Keep the rest of the image unchanged as much as possible.
        Preserve the original background, environment, composition, lighting, perspective, camera angle, colors, proportions, and surrounding details unless the user specifically asks to change them.
        The edited element should blend naturally into the original image with matching lighting, shadows, reflections, texture quality, depth, and perspective.
        Do not reinterpret the entire image. Do not change unrelated objects, people, background elements, scene structure, or overall composition.
        The result should look like the same image with only the requested element creatively varied according to the user's wishes.
        Do not create multiple variations. Generate only one varied image.
    `;
};

export const expandImagePrompt = (
    positivePrompt: string,
    negativePrompt: string | null
) => {
    return {
        positive_prompt: `
            Expand the image by filling only the transparent or newly created canvas areas.

            User request:
            "${positivePrompt}"

            The existing visible image content is the source of truth and must remain completely unchanged.

            Important:
            Only generate new content inside the transparent expanded areas.
            Do not modify any existing visible pixels of the original image.
            Do not repaint, regenerate, enhance, reinterpret, restyle, move, replace, or alter any existing part of the image.

            Treat the original image as locked and immutable.

            The transparent areas must be filled with meaningful image content that naturally continues the existing scene.
            Do not fill transparent areas with plain black, solid colors, flat color blocks, empty gradients, or single-color backgrounds.

            Use the visual information directly adjacent to each transparent region as the primary source for generation.
            Continue nearby textures, structures, surfaces, colors, lighting, perspective, depth, patterns, atmosphere, and visual style into the transparent areas.

            Generate content only at the expanded borders or transparent regions where the canvas was extended.
            Do not add or change content anywhere else in the image.

            The generated areas should feel like a natural continuation of the original image, as if the image had originally been captured on a larger canvas.

            Preserve:
            - original subject
            - original composition
            - original environment
            - original lighting
            - original colors
            - original materials
            - original style
            - original atmosphere
            - original level of detail
            - all existing visible image regions

            Prefer:
            - scene continuation over invention
            - structure extension over reinterpretation
            - contextual background completion over flat filling
            - seamless transitions over visible borders
            - environmental consistency over creativity

            The boundary between original and generated areas must be seamless and visually invisible.

            The final result should look exactly like the original image extended beyond its borders, with only the transparent regions filled naturally and consistently.
        `.replace(/\s+/g, " ").trim(),

        negative_prompt: negativePrompt === null
            ? null
            : `
                ${negativePrompt}

                Do not modify existing visible image content.
                Do not change the original subject, composition, colors, lighting, style, or environment.
                Do not generate content outside transparent expanded areas.
                No black fill, no solid color fill, no flat background, no empty gradient fill.
                No unrelated objects, no new subjects, no replacement objects, no scene reinterpretation.
                No visible seams, no hard borders, no mask edges, no artifacts, no blurry patches, no duplicated textures, no inconsistent perspective, no broken structures, no unnatural transitions.
            `.replace(/\s+/g, " ").trim()
    };
};
export const spellCorrectionPrompt = (
    text: string,
    positivePrompt?: string | null,
    negativePrompt?: string | null
) => {
    const hasPositivePrompt = !!positivePrompt?.trim();
    const hasNegativePrompt = !!negativePrompt?.trim();

    return `
        Correct the following text:

        "${text}"

        Primary goal:
        Correct spelling, grammar, punctuation, capitalization, wording, readability, and clarity issues while preserving the original meaning and intent of the text.

        ${
            hasPositivePrompt
                ? `
        Additional user instructions:
        "${positivePrompt}"

        Apply these instructions where appropriate while performing the correction.
        `
                : ''
        }

        ${
            hasNegativePrompt
                ? `
        Important restrictions:
        "${negativePrompt}"

        Do not perform any changes that would violate these restrictions.
        `
                : ''
        }

        Keep:
        - the original meaning
        - the original intent
        - the original subject matter
        - the original structure whenever possible

        Improve:
        - spelling mistakes
        - grammar mistakes
        - punctuation errors
        - capitalization issues
        - awkward wording
        - readability
        - linguistic clarity

        Do not:
        - invent new content
        - add new information
        - change the topic
        - rewrite the text into a different style unless explicitly requested
        - remove important information

        Return only the corrected text.
    `;
};
export const removeObjectPrompt = (prompt: string) => {
    return {
        positive: `
            Fill the transparent or missing area naturally.

            User request:
            "${prompt}"

            The image already contains a transparent or empty region where content was removed.
            Do not search for or remove another object.

            Reconstruct only the transparent or missing area by continuing the surrounding background,
            nearby textures, surfaces, patterns, colors, perspective, lighting, and shadows.

            The filled area must blend seamlessly with the existing scene.
            Preserve all visible image content outside the transparent area exactly as it is.

            Do not add a new main object, new subject, character, logo, text, UI element, weapon,
            decoration, or unrelated detail.

            The final image should look as if the transparent area had always been part of the original background.
        `.trim(),

        negative: `
            new object, replacement object, inserted subject, extra person, extra animal,
            extra item, logo, text, watermark, UI, HUD, weapon, game menu,
            unrelated object, scene change, background invention, visible patch,
            blurry patch, smearing, warped texture, distorted perspective,
            broken structure, hard edge, transparent area remaining, hole, artifact
        `.trim()
    };
};
export const extractObjectPrompt = () => {
    return `
        Extract the object identified by the provided object mask.

        This is a cutout and isolation task.

        The object mask already represents the target object that should be extracted.

        Keep only the detected object inside the object mask.

        Remove everything else from the image.

        Completely discard:
        - the background
        - surrounding scenery
        - environment details
        - other objects
        - people
        - shadows outside the object
        - reflections outside the object
        - all image content not belonging to the extracted object

        The output must contain only the isolated object itself.

        Use the object mask to determine the object's full visible boundaries and extract the complete object, including small details that may extend slightly beyond the mask edges.

        Preserve:
        - exact object shape
        - object silhouette
        - colors
        - textures
        - transparency
        - fine details
        - material appearance
        - lighting information that belongs to the object itself

        Remove:
        - all surrounding pixels
        - all non-object regions
        - all background pixels
        - all unrelated image content

        The extracted object should be tightly cropped around its visible boundaries.

        The area outside the extracted object must be fully transparent.

        Do not:
        - return the original image
        - keep the original background
        - create a background
        - fill transparent areas
        - reconstruct missing regions
        - perform object removal
        - perform inpainting
        - leave large parts of the original image intact

        The final result should be a clean cutout of the detected object on a transparent background, ready to be used as a separate image layer.
    `.replace(/\\s+/g, " ").trim();
};
export const improveImageGenerationPrompt = (
    positivePrompt: string,
    negativePrompt: string | null,
    type: 'create' | 'edit'
) => {
    if (type === 'edit') {
        return `
            You are an advanced image editing prompt optimizer.

            Your task is to improve the provided prompt for editing an existing image.

            Input positive prompt:
            "${positivePrompt}"

            Input negative prompt:
            ${negativePrompt ? `"${negativePrompt}"` : "null"}

            Important goal:
            Improve the user's editing instruction so it clearly describes what should be changed, adjusted, added, removed, corrected, replaced, enhanced, or transformed in the existing image.

            The prompt must be suitable for image editing, not full image creation.

            Instructions for the positive prompt:
            - Preserve the user's original editing intent.
            - Make the edit request clearer, more precise, and easier for an image editing model to follow.
            - Focus on the specific change that should happen.
            - Keep the instruction compatible with any kind of edit, such as object changes, background changes, style changes, corrections, cleanup, enhancement, replacement, color edits, text edits, or localized edits.
            - Assume there is already an existing image.
            - Do not describe a completely new image unless the user explicitly asks for a full transformation.
            - Do not unnecessarily change unrelated parts of the image.
            - Do not add complex scene details that were not requested.
            - Keep the improved prompt concise, direct, and editing-focused.
            - The result should describe what should be edited and how it should integrate with the existing image.

            Instructions for the negative prompt:
            - If a negative prompt exists, improve it only by clarifying what should be avoided during the edit.
            - Include general editing-related exclusions where appropriate, such as unwanted artifacts, distorted details, blurry edits, inconsistent lighting, unnatural edges, visible seams, incorrect perspective, unwanted object changes, or changes to unrelated areas.
            - Do not add style-specific negative terms unless they were already present.
            - If the negative prompt is null, it MUST remain null.
            - Never invent a negative prompt when none was provided.

            Return ONLY valid JSON.
            Do not include markdown, explanations, comments, or additional text.

            Required JSON format:
            {
                "positive_prompt": string,
                "negative_prompt": string | null
            }
        `;
    }

    return `
        You are an advanced image prompt optimizer.

        Your task is to improve the provided image generation prompt by refining the scene description only.

        Input positive prompt:
        "${positivePrompt}"

        Input negative prompt:
        ${negativePrompt ? `"${negativePrompt}"` : "null"}

        Important goal:
        Improve the actual scenario, subject, setting, objects, actions, spatial arrangement, and visual clarity of the prompt.
        Do NOT strongly define or override a specific art style, rendering style, medium, mood, atmosphere, or color grading, because a separate style system may be applied later during image generation.

        Instructions for the positive prompt:
        - Preserve the original intent, subject, setting, and core idea.
        - Improve clarity, structure, and descriptive precision.
        - Expand the scene itself with useful details such as subject placement, environment, objects, actions, scale, perspective, and composition.
        - Keep the description compatible with any later selected style.
        - Avoid adding strong style terms such as cinematic, anime, oil painting, watercolor, cyberpunk, gothic, fantasy, photorealistic, 3D render, comic, or similar unless they were explicitly part of the original prompt.
        - Avoid over-defining mood, lighting, color palette, atmosphere, or artistic rendering.
        - The result should describe WHAT should be generated, not HOW it should be styled.
        - Make the prompt clean, detailed, and suitable as a base prompt for later style extension.

        Instructions for the negative prompt:
        - If a negative prompt exists, improve it only by clarifying unwanted image problems and preserving its intent.
        - You may include general quality exclusions such as blurry, distorted, artifacts, bad anatomy, low detail, text errors, watermark, duplicate elements, or unrealistic proportions.
        - Do not add style-specific negative terms unless they were already present.
        - If the negative prompt is null, it MUST remain null.
        - Never invent a negative prompt when none was provided.

        Return ONLY valid JSON.
        Do not include markdown, explanations, comments, or additional text.

        Required JSON format:
        {
            "positive_prompt": string,
            "negative_prompt": string | null
        }
    `;
};
export const randomImagePromptGenerator = (
    negativePrompt: boolean,
    type: 'create' | 'edit'
) => {
    if (type === 'edit') {
        return `
            You are a creative image editing prompt generator.

            Generate one random image editing prompt for an already existing image.

            Important goal:
            The generated prompt should describe a creative but generic edit that can work on almost any image. It should not require a specific subject, object, person, place, or scene.

            The edit should focus mainly on:
            - atmosphere changes
            - color adjustments
            - mood transformation
            - creative visual treatment
            - lighting enhancement
            - subtle stylistic reinterpretation
            - background feeling
            - overall visual polish

            The generated positive prompt should:
            - be suitable for editing an existing image
            - preserve the original subject, composition, and scene structure
            - avoid replacing the image with a completely new scene
            - avoid adding specific new objects, people, animals, text, logos, buildings, or props
            - describe a clear visual edit that can be applied broadly
            - feel creative, useful, and visually interesting
            - remain compatible with any later selected style
            - focus on HOW the existing image should be adjusted, not WHAT new scene should be created

            Good examples of edit directions:
            - make the image feel warmer and more inviting
            - add a soft dreamy atmosphere
            - create a dramatic high-contrast look
            - shift the colors toward a calm pastel palette
            - enhance depth, clarity, and visual harmony
            - add subtle glowing highlights and richer shadows
            - create a moody evening atmosphere
            - make the scene feel more magical and artistic without changing the subject

            Avoid:
            - generating a new main subject
            - changing the identity of the image
            - replacing the background completely
            - adding unrelated objects or characters
            - strong scene-specific instructions
            - overly complex transformations
            - style terms that may conflict with a separate style system, unless the edit itself requires a broad creative treatment

            ${
                negativePrompt
                    ? `
            Also generate a general negative prompt for image editing.
            The negative prompt should include common unwanted edit issues such as blurry edits, visible seams, artifacts, distorted details, inconsistent lighting, unnatural colors, overprocessed results, unwanted object changes, changed identity, altered composition, text artifacts, watermark, low quality, and changes to unrelated areas.
            Do not include highly style-specific exclusions unless necessary.
            `
                    : `
            Do not generate a negative prompt.
            The negative_prompt field must be null.
            `
            }

            Return ONLY valid JSON in the following format:
            {
                "positive_prompt": string,
                "negative_prompt": string | null
            }

            Important:
            - Do not include markdown formatting.
            - Do not include explanations or comments.
            - The response must be valid parsable JSON only.
            - If negativePrompt is false, negative_prompt MUST be null.
        `;
    }

    return `
        You are a creative image scenario generator.

        Generate one unique random image generation prompt that describes an interesting scene, subject, action, setting, and visual concept.

        Important goal:
        Create a strong base scenario only.
        Do NOT strongly define a specific art style, rendering medium, mood, atmosphere, color grading, or visual style, because a separate style system may be applied later during image generation.

        The generated positive prompt should:
        - describe a clear main subject
        - include an interesting setting or environment
        - include meaningful objects, actions, or details
        - define spatial arrangement and composition in a simple way
        - be creative, unusual, or visually memorable
        - remain flexible enough to work with any later selected style
        - focus on WHAT is shown, not HOW it is artistically rendered

        Avoid:
        - strong style labels such as cinematic, anime, oil painting, watercolor, comic, cyberpunk, gothic, fantasy art, photorealistic, 3D render, digital art, or similar
        - heavy mood descriptions
        - detailed lighting instructions
        - fixed color palettes
        - excessive atmosphere descriptions
        - instructions that could conflict with a later selected style

        ${
            negativePrompt
                ? `
        Also generate a general negative prompt.
        The negative prompt should include common unwanted image generation issues such as blurry details, distortion, artifacts, bad anatomy, incorrect proportions, duplicate objects, text artifacts, watermark, low quality, cropped subject, messy composition, and unwanted noise.
        Do not include style-specific exclusions unless necessary.
        `
                : `
        Do not generate a negative prompt.
        The negative_prompt field must be null.
        `
        }

        Return ONLY valid JSON in the following format:
        {
            "positive_prompt": string,
            "negative_prompt": string | null
        }

        Important:
        - Do not include markdown formatting.
        - Do not include explanations or comments.
        - The response must be valid parsable JSON only.
        - If negativePrompt is false, negative_prompt MUST be null.
    `;
};
export const improveVideoGenerationPrompt = (
    positivePrompt: string,
    negativePrompt: string | null,
    type: 'create' | 'edit'
) => {
    if (type === 'edit') {
        return `
            You are an advanced video editing prompt optimizer.

            Your task is to improve the provided prompt for editing an existing video.

            Input positive prompt:
            "${positivePrompt}"

            Input negative prompt:
            ${negativePrompt ? `"${negativePrompt}"` : "null"}

            Important goal:
            Improve the user's editing instruction so it clearly describes what should be changed, adjusted, added, removed, corrected, replaced, enhanced, or transformed in the existing video.

            The prompt must be suitable for video editing, not full video creation.

            Instructions for the positive prompt:
            - Preserve the user's original editing intent.
            - Make the edit request clearer, more precise, and easier for a video editing model to follow.
            - Focus on the specific change that should happen.
            - Preserve temporal consistency between frames.
            - Clearly describe changes to movement, timing, camera motion, subjects, objects, environment, or visual properties when relevant.
            - Keep the instruction compatible with edits such as object changes, background changes, motion changes, color edits, cleanup, enhancement, replacement, stabilization, or localized edits.
            - Assume there is already an existing video.
            - Preserve the original subject, scene structure, motion, and camera behavior unless the user explicitly requests changes to them.
            - Do not describe a completely new video unless the user explicitly asks for a full transformation.
            - Do not unnecessarily change unrelated parts of the video.
            - Avoid introducing temporal inconsistencies, flickering, sudden object changes, or unintended motion.
            - Keep the improved prompt concise, direct, and editing-focused.
            - The result should describe what should be edited and how the change should remain consistent throughout the video.

            Instructions for the negative prompt:
            - If a negative prompt exists, improve it only by clarifying what should be avoided during the edit.
            - Include general video-editing exclusions where appropriate, such as flickering, temporal inconsistency, jitter, distorted motion, warped objects, unstable subjects, sudden appearance changes, visible seams, frame artifacts, unnatural motion, unwanted camera movement, or changes to unrelated areas.
            - Do not add style-specific negative terms unless they were already present.
            - If the negative prompt is null, it MUST remain null.
            - Never invent a negative prompt when none was provided.

            Return ONLY valid JSON.
            Do not include markdown, explanations, comments, or additional text.

            Required JSON format:
            {
                "positive_prompt": string,
                "negative_prompt": string | null
            }
        `;
    }

    return `
        You are an advanced video generation prompt optimizer.

        Your task is to improve the provided video generation prompt by refining the scene description, action, movement, timing, and camera behavior only.

        Input positive prompt:
        "${positivePrompt}"

        Input negative prompt:
        ${negativePrompt ? `"${negativePrompt}"` : "null"}

        Important goal:
        Improve the actual scenario, subject, setting, objects, actions, movement, temporal progression, spatial arrangement, camera behavior, and visual clarity of the prompt.
        Do NOT strongly define or override a specific art style, rendering style, medium, mood, atmosphere, or color grading, because a separate style system may be applied later during video generation.

        Instructions for the positive prompt:
        - Preserve the original intent, subject, setting, action, and core idea.
        - Improve clarity, structure, and descriptive precision.
        - Clearly describe the main subject and environment.
        - Improve actions and movement so the intended motion is understandable.
        - Describe temporal progression when useful: what happens first, how the action develops, and how it ends.
        - Add useful camera behavior such as static camera, pan, tilt, tracking, zoom, dolly, or orbit only when it supports the user's intent.
        - Keep movements physically and spatially coherent.
        - Preserve continuity of subjects, objects, environment, and camera throughout the sequence.
        - Expand the scene with useful details such as subject placement, environment, objects, actions, scale, perspective, depth, and composition.
        - Keep the description compatible with any later selected style.
        - Avoid adding strong style terms such as cinematic, anime, oil painting, watercolor, cyberpunk, gothic, fantasy, photorealistic, 3D render, comic, or similar unless they were explicitly part of the original prompt.
        - Avoid over-defining mood, lighting, color palette, atmosphere, or artistic rendering.
        - The result should describe WHAT happens in the video and HOW elements move, not HOW it should be artistically styled.
        - Make the prompt clean, detailed, temporally coherent, and suitable as a base prompt for later style extension.

        Instructions for the negative prompt:
        - If a negative prompt exists, improve it only by clarifying unwanted video generation problems and preserving its intent.
        - You may include general quality exclusions such as flickering, temporal inconsistency, jitter, unnatural motion, warped objects, distorted anatomy, unstable subjects, duplicate objects, sudden appearance changes, frame artifacts, text errors, watermark, low detail, or unwanted camera movement.
        - Do not add style-specific negative terms unless they were already present.
        - If the negative prompt is null, it MUST remain null.
        - Never invent a negative prompt when none was provided.

        Return ONLY valid JSON.
        Do not include markdown, explanations, comments, or additional text.

        Required JSON format:
        {
            "positive_prompt": string,
            "negative_prompt": string | null
        }
    `;
};
export const randomVideoPromptGenerator = (
    negativePrompt: boolean,
    type: 'create' | 'edit'
) => {
    if (type === 'edit') {
        return `
            You are a creative video editing prompt generator.

            Generate one random video editing prompt for an already existing video.

            Important goal:
            The generated prompt should describe a creative but generic edit that can work on many different videos.
            It should not require a specific subject, person, animal, object, place, or scene.

            The edit should focus mainly on:
            - movement enhancement
            - timing adjustments
            - camera motion
            - stabilization
            - atmosphere changes
            - color adjustments
            - lighting enhancement
            - temporal visual treatment
            - overall visual polish

            The generated positive prompt should:
            - be suitable for editing an existing video
            - preserve the original subject, scene, composition, and general sequence
            - preserve temporal consistency
            - avoid replacing the video with a completely new scene
            - avoid adding specific new objects, people, animals, text, logos, buildings, or props
            - describe a clear visual or motion-related edit that can be applied broadly
            - avoid sudden changes between frames
            - keep subjects and objects visually consistent throughout the video
            - feel creative, useful, and visually interesting
            - remain compatible with any later selected style
            - focus on HOW the existing video should be adjusted, not WHAT new video should be created

            Good examples of edit directions:
            - smooth the existing motion and make movements feel more natural
            - introduce a subtle slow camera push while preserving the original scene
            - create smoother transitions in movement and timing
            - make the sequence feel warmer while maintaining consistent colors between frames
            - enhance depth, clarity, and temporal consistency
            - stabilize distracting camera movement while preserving intentional motion
            - slightly slow down important movement for a more deliberate progression
            - enhance lighting changes smoothly throughout the sequence

            Avoid:
            - generating a new main subject
            - changing the identity of existing subjects
            - replacing the scene completely
            - adding unrelated objects or characters
            - strong scene-specific instructions
            - impossible or contradictory movement
            - flickering or inconsistent changes between frames
            - overly complex transformations
            - style terms that may conflict with a separate style system

            ${
                negativePrompt
                    ? `
            Also generate a general negative prompt for video editing.
            The negative prompt should include common unwanted issues such as flickering, jitter, temporal inconsistency, unstable subjects, warped objects, unnatural movement, sudden appearance changes, frame artifacts, inconsistent lighting, unwanted camera movement, text artifacts, watermark, low quality, and changes to unrelated areas.
            Do not include highly style-specific exclusions unless necessary.
            `
                    : `
            Do not generate a negative prompt.
            The negative_prompt field must be null.
            `
            }

            Return ONLY valid JSON in the following format:
            {
                "positive_prompt": string,
                "negative_prompt": string | null
            }

            Important:
            - Do not include markdown formatting.
            - Do not include explanations or comments.
            - The response must be valid parsable JSON only.
            - If negativePrompt is false, negative_prompt MUST be null.
        `;
    }

    return `
        You are a creative video scenario generator.

        Generate one unique random video generation prompt that describes an interesting subject, scene, action, movement, setting, temporal progression, and visual concept.

        Important goal:
        Create a strong base video scenario only.
        Do NOT strongly define a specific art style, rendering medium, mood, atmosphere, color grading, or visual style, because a separate style system may be applied later during video generation.

        The generated positive prompt should:
        - describe a clear main subject
        - include an interesting setting or environment
        - include meaningful objects, actions, or details
        - describe clear and visually understandable movement
        - describe how the action develops over time
        - define spatial arrangement and composition in a simple way
        - include useful camera behavior when appropriate
        - keep subject and object movement spatially coherent
        - maintain visual and temporal continuity
        - be creative, unusual, or visually memorable
        - remain flexible enough to work with any later selected style
        - focus on WHAT is shown and WHAT happens, not HOW it is artistically rendered

        Avoid:
        - strong style labels such as cinematic, anime, oil painting, watercolor, comic, cyberpunk, gothic, fantasy art, photorealistic, 3D render, digital art, or similar
        - heavy mood descriptions
        - detailed lighting instructions
        - fixed color palettes
        - excessive atmosphere descriptions
        - contradictory or impossible movement
        - unnecessarily complicated sequences
        - instructions that could conflict with a later selected style

        ${
            negativePrompt
                ? `
        Also generate a general negative prompt.
        The negative prompt should include common unwanted video generation issues such as flickering, jitter, temporal inconsistency, unnatural movement, warped objects, distorted anatomy, unstable subjects, duplicate objects, sudden appearance changes, frame artifacts, text artifacts, watermark, low quality, messy composition, and unwanted camera movement.
        Do not include style-specific exclusions unless necessary.
        `
                : `
        Do not generate a negative prompt.
        The negative_prompt field must be null.
        `
        }

        Return ONLY valid JSON in the following format:
        {
            "positive_prompt": string,
            "negative_prompt": string | null
        }

        Important:
        - Do not include markdown formatting.
        - Do not include explanations or comments.
        - The response must be valid parsable JSON only.
        - If negativePrompt is false, negative_prompt MUST be null.
    `;
};
export const improveGifGenerationPrompt = (
    positivePrompt: string,
    negativePrompt: string | null,
    type: 'create' | 'edit'
) => {
    if (type === 'edit') {
        return `
            You are an advanced GIF editing prompt optimizer.

            Your task is to improve the provided prompt for editing an existing animated GIF.

            Input positive prompt:
            "${positivePrompt}"

            Input negative prompt:
            ${negativePrompt ? `"${negativePrompt}"` : "null"}

            Important goal:
            Improve the user's editing instruction so it clearly describes what should be changed while preserving a short, coherent animation and, where appropriate, a smooth repeating loop.

            The prompt must be suitable for editing an existing GIF, not creating a completely new animation.

            Instructions for the positive prompt:
            - Preserve the user's original editing intent.
            - Make the edit request clearer, more precise, and easier for an animation editing model to follow.
            - Focus on the specific visual or motion-related change that should happen.
            - Assume there is already an existing animated GIF.
            - Preserve the original subject, composition, scene structure, and animation unless explicitly requested otherwise.
            - Preserve temporal consistency between frames.
            - Keep movement clear and suitable for a short animation.
            - Preserve or improve a seamless loop when the existing animation is intended to loop.
            - Avoid sudden jumps between the final and first frame.
            - Do not unnecessarily change unrelated parts of the GIF.
            - Do not add complex scene details that were not requested.
            - Keep the improved prompt concise, direct, and editing-focused.
            - The result should describe what should be edited and how the change should remain visually consistent throughout the animation.

            Instructions for the negative prompt:
            - If a negative prompt exists, improve it only by clarifying what should be avoided during the edit.
            - Include general GIF-editing exclusions where appropriate, such as flickering, jitter, broken loops, abrupt frame transitions, temporal inconsistency, warped objects, unstable subjects, frame artifacts, unnatural motion, or unwanted changes to unrelated areas.
            - Do not add style-specific negative terms unless they were already present.
            - If the negative prompt is null, it MUST remain null.
            - Never invent a negative prompt when none was provided.

            Return ONLY valid JSON.
            Do not include markdown, explanations, comments, or additional text.

            Required JSON format:
            {
                "positive_prompt": string,
                "negative_prompt": string | null
            }
        `;
    }

    return `
        You are an advanced animated GIF generation prompt optimizer.

        Your task is to improve the provided GIF generation prompt by refining the scene, subject, action, movement, and looping behavior.

        Input positive prompt:
        "${positivePrompt}"

        Input negative prompt:
        ${negativePrompt ? `"${negativePrompt}"` : "null"}

        Important goal:
        Improve the actual scenario, subject, setting, objects, action, movement, spatial arrangement, temporal clarity, and loop behavior.
        Do NOT strongly define or override a specific art style, rendering style, medium, mood, atmosphere, or color grading, because a separate style system may be applied later.

        Instructions for the positive prompt:
        - Preserve the original intent, subject, setting, action, and core idea.
        - Improve clarity, structure, and descriptive precision.
        - Keep the animation focused on one clear primary action or a small number of closely related actions.
        - Describe movement clearly and concisely.
        - Keep the temporal sequence short and easy to understand.
        - Make the motion suitable for repeated playback.
        - Prefer a seamless loop when compatible with the user's intent.
        - When appropriate, describe how the final state naturally reconnects to the initial state.
        - Avoid abrupt changes between the last and first frame.
        - Keep subjects, objects, proportions, and positions temporally consistent.
        - Keep camera movement simple unless more complex movement was explicitly requested.
        - Keep the description compatible with any later selected style.
        - Avoid adding strong style terms unless they were explicitly part of the original prompt.
        - Avoid over-defining mood, lighting, color palette, atmosphere, or artistic rendering.
        - The result should describe WHAT is animated and HOW it moves, not HOW it should be artistically styled.

        Instructions for the negative prompt:
        - If a negative prompt exists, improve it only by clarifying unwanted GIF generation problems and preserving its intent.
        - You may include general quality exclusions such as flickering, jitter, broken loop, abrupt transitions, inconsistent frames, unnatural motion, warped objects, unstable subjects, duplicate elements, frame artifacts, text errors, watermark, or low detail.
        - Do not add style-specific negative terms unless they were already present.
        - If the negative prompt is null, it MUST remain null.
        - Never invent a negative prompt when none was provided.

        Return ONLY valid JSON.
        Do not include markdown, explanations, comments, or additional text.

        Required JSON format:
        {
            "positive_prompt": string,
            "negative_prompt": string | null
        }
    `;
};
export const randomGifPromptGenerator = (
    negativePrompt: boolean,
    type: 'create' | 'edit'
) => {
    if (type === 'edit') {
        return `
            You are a creative animated GIF editing prompt generator.

            Generate one random editing prompt for an already existing animated GIF.

            Important goal:
            Generate a creative but generic edit that can work on many different GIFs while preserving the existing subject, scene, and short animation.

            The edit should focus mainly on:
            - motion refinement
            - loop improvement
            - timing adjustments
            - subtle animation enhancement
            - color adjustments
            - lighting enhancement
            - visual polish
            - temporal consistency

            The generated positive prompt should:
            - be suitable for editing an existing animated GIF
            - preserve the original subject, composition, and scene structure
            - preserve the identity of existing subjects
            - keep the animation short and visually clear
            - preserve or improve a smooth repeating loop
            - avoid abrupt transitions between the final and first frame
            - avoid adding specific new objects, people, animals, text, logos, buildings, or props
            - avoid replacing the animation with a completely new scene
            - maintain consistent objects and subjects between frames
            - describe a useful edit that can apply broadly
            - remain compatible with any later selected style
            - focus on HOW the existing GIF should be adjusted

            Good examples of edit directions:
            - smooth the existing animation and create a more seamless repeating loop
            - make the movement slightly more fluid and consistent between frames
            - enhance clarity and color while preserving the original animation
            - improve the timing so the main motion feels more natural
            - create a subtle rhythmic motion that returns smoothly to its starting state
            - reduce distracting frame-to-frame inconsistencies
            - enhance the existing movement without changing the subject or composition

            Avoid:
            - generating a new main subject
            - changing the identity of existing subjects
            - replacing the scene completely
            - adding unrelated objects or characters
            - complex multi-stage sequences
            - abrupt non-looping endings
            - inconsistent movement between frames
            - style terms that may conflict with a separate style system

            ${
                negativePrompt
                    ? `
            Also generate a general negative prompt for GIF editing.
            The negative prompt should include common unwanted issues such as flickering, jitter, broken loops, abrupt transitions, inconsistent frames, warped objects, unstable subjects, unnatural movement, duplicate elements, frame artifacts, text artifacts, watermark, low quality, and unwanted changes to unrelated areas.
            Do not include highly style-specific exclusions unless necessary.
            `
                    : `
            Do not generate a negative prompt.
            The negative_prompt field must be null.
            `
            }

            Return ONLY valid JSON in the following format:
            {
                "positive_prompt": string,
                "negative_prompt": string | null
            }

            Important:
            - Do not include markdown formatting.
            - Do not include explanations or comments.
            - The response must be valid parsable JSON only.
            - If negativePrompt is false, negative_prompt MUST be null.
        `;
    }

    return `
        You are a creative animated GIF scenario generator.

        Generate one unique random GIF generation prompt describing a visually clear subject, setting, and short repeating action.

        Important goal:
        Create a strong base animation concept suitable for a short GIF.
        Prefer a simple, visually recognizable action that can form a natural seamless loop.
        Do NOT strongly define a specific art style, rendering medium, mood, atmosphere, color grading, or visual style, because a separate style system may be applied later.

        The generated positive prompt should:
        - describe a clear main subject
        - include a simple but interesting setting or environment
        - define one primary repeating action or motion
        - make the movement immediately understandable
        - keep the sequence short and focused
        - make the final state naturally reconnect to the initial state when possible
        - create a smooth and visually coherent loop
        - maintain consistent subject appearance, proportions, and position throughout the animation
        - keep camera movement minimal unless it contributes naturally to the loop
        - include useful spatial details without overcomplicating the animation
        - be creative, unusual, playful, useful, or visually memorable
        - remain flexible enough to work with any later selected style
        - focus on WHAT is shown and WHAT moves, not HOW it is artistically rendered

        Avoid:
        - strong style labels such as cinematic, anime, oil painting, watercolor, comic, cyberpunk, gothic, fantasy art, photorealistic, 3D render, digital art, or similar
        - complex narratives or many consecutive actions
        - movement that cannot naturally repeat
        - abrupt endings
        - unnecessary scene changes
        - excessive camera movement
        - heavy mood descriptions
        - detailed lighting instructions
        - fixed color palettes
        - instructions that could conflict with a later selected style

        ${
            negativePrompt
                ? `
        Also generate a general negative prompt.
        The negative prompt should include common unwanted GIF generation issues such as flickering, jitter, broken loops, abrupt transitions, inconsistent frames, warped objects, unstable subjects, unnatural movement, duplicate elements, frame artifacts, text artifacts, watermark, low quality, and visual discontinuity.
        Do not include style-specific exclusions unless necessary.
        `
                : `
        Do not generate a negative prompt.
        The negative_prompt field must be null.
        `
        }

        Return ONLY valid JSON in the following format:
        {
            "positive_prompt": string,
            "negative_prompt": string | null
        }

        Important:
        - Do not include markdown formatting.
        - Do not include explanations or comments.
        - The response must be valid parsable JSON only.
        - If negativePrompt is false, negative_prompt MUST be null.
    `;
};